import type { FC } from 'react';
import React, { useEffect, useCallback, useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';

import CommonSidePanelComponent from '@atlassian/audit-logs-side-panel';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@confluence/conversation-assistant/entry-points/containerUtils.ts';
import { VIEW_IN_PRODUCT_HELP_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { HelpPanel } from '@confluence/in-product-help/entry-points/HelpPanel.ts';
import {
	useInProductHelpActions,
	useInProductHelpArticleId,
} from '@confluence/in-product-help/entry-points/InProductHelpProvider.ts';
import { getRouteData } from '@confluence/in-product-help/entry-points/inProductHelpUtil.ts';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
} from '@confluence/in-product-help/entry-points/constants.ts';
import { expVal } from '@confluence/feature-experiments';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BulkCreateSidePanel,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import {
	IssueCreateSidePanel,
	ISSUE_CREATE_SIDE_PANEL_ID,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanel.ts';
import { fg } from '@confluence/feature-gating';
import {
	AUDIT_LOG_DETAILS_PANEL,
	getAuditLogEvent,
	clearAuditLogEvent,
} from '@confluence/audit-log/entry-points/auditLogPanel.ts';
import { APPROVALS_PANE_ID } from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { ApprovalsPane } from '@confluence/approvals/entry-points/ApprovalsPane.ts';

// Common Component Audit Log Side Panel
const AuditLogDetailsPanel: FC = () => {
	const rightSidebarContext = useRightSidebarContext();
	const selectedEvent = getAuditLogEvent();

	const handleClose = () => {
		clearAuditLogEvent(); // Clear the event when closing
		rightSidebarContext.close(AUDIT_LOG_DETAILS_PANEL);
	};

	if (!selectedEvent) {
		return null;
	}

	// Transform the data to match the common component's expected format
	const transformedEvent = {
		actor: {
			name: selectedEvent.attributes?.actor?.name || '',
			email: selectedEvent.attributes?.actor?.email || '',
			accountId: selectedEvent.attributes?.actor?.id || '',
			avatarUrl: selectedEvent.attributes?.actor?.picture || '',
		},
		details: {
			action: selectedEvent.attributes?.action || '',
			activityId: selectedEvent.id || '',
			date: selectedEvent.attributes?.time || '',
			category: 'audit',
			activity: {
				message: selectedEvent.message?.content || '',
				type: selectedEvent.message?.format || 'simple',
			},
		},
		location: {
			ip: selectedEvent.attributes?.location?.ip || '',
			city: selectedEvent.attributes?.location?.city || '',
		},
	};

	// Create minimal related events data
	const relatedEvents = {
		correlationId: selectedEvent.attributes?.correlationId || '',
		hasMoreEvents: false,
		relatedEvents: [],
		relatedEventsLink: '',
		totalEventsCount: 0,
	};

	return (
		<CommonSidePanelComponent
			sidePanel={{
				activeMode: 'activity',
				activity: {
					activityData: transformedEvent as any,
					activeTab: 'details',
					relatedEvents,
				},
			}}
			onClose={handleClose}
		/>
	);
};

export type RightPanelProps = {
	toggleInProductHelp: (val) => void;
};

const i18n = defineMessages({
	panelSplitter: {
		id: 'page-layout.right-panel.resize.label',
		defaultMessage: 'Resize panel',
		description: 'label text for a button that will allow resizing of the right side panel',
	},
	auditLogClose: {
		id: 'page-layout.audit-log-panel.close.label',
		defaultMessage: 'Close',
		description: 'label text for the close button in the audit log side panel',
	},
	auditLogContent: {
		id: 'page-layout.audit-log-panel.content.label',
		defaultMessage: 'Simple side panel content',
		description: 'content text for the audit log side panel',
	},
});

export const RightPanel: FC<RightPanelProps> = ({ toggleInProductHelp }) => {
	const {
		isLoggedIn,
		isRovoEnabled: isRovoEnabledSessionData,
		isRovoLLMEnabled,
	} = useSessionData();
	const shouldShowRovo = fg('rovo_use_rovo_llm_enabled_check')
		? isRovoLLMEnabled
		: isRovoEnabledSessionData;

	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isRovoEnabled = shouldShowRovo && isLoggedIn;

	const { location, push } = useContext(RoutesContext);

	const [routeData, setRouteData] = useState({
		routeName: '',
		routeGroup: '',
	});
	const inProductHelpContext = useInProductHelpActions();
	const articleId = useInProductHelpArticleId();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleRovoPanelResizeComplete = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'resized',
				actionSubject: 'aiMatePanel',
				source: 'confluencePanel',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	useEffect(() => {
		const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
		if (value) {
			try {
				const { panelId, articleId: localStorageArticleId } = JSON.parse(value);
				// Check if there was a specific article previously opened
				if (panelId === HELP_PANEL_ID && localStorageArticleId) {
					inProductHelpContext.setArticleId(localStorageArticleId);
				}
			} catch (e) {
				experienceTracker.stopOnError({
					name: VIEW_IN_PRODUCT_HELP_EXPERIENCE,
					error: e,
				});
			}
		}

		if (location) {
			const { routeName, routeGroup } = getRouteData(location.pathname);
			setRouteData({ routeName, routeGroup });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, experienceTracker]);

	// Open a panel if one is persisted in local storage
	useEffect(() => {
		const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
		if (value) {
			const { panelId, panelWidth } = JSON.parse(value);

			const excludedPanels = [CONVERSATION_ASSISTANT_CONTAINER_ID];
			if (expVal('cc_stabilize_main_content_class', 'isEnabled', false)) {
				// The help panel is not SSRd and causes a high-volume VC90 offender ("confluence-main-content-compiled")
				// if this experiment is enabled, skip opening this panel post hydration if its in local storage
				excludedPanels.push(HELP_PANEL_ID);
			}
			const isOtherPanel = !excludedPanels.includes(panelId);

			if (isOtherPanel) {
				rightSidebarContext.open(panelId, panelWidth);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const rightSidebarContext = useRightSidebarContext();

	// Remove sidebar item from local storage if no sidebars open in context
	useEffect(() => {
		if (rightSidebarContext.sidebars.length === 0) {
			const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
			if (value) {
				confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);
			}
		}
	}, [rightSidebarContext]);

	const rightSidebarCurrentState = rightSidebarContext.getCurrent();

	const handleOnCloseHelpPanel = () => {
		rightSidebarContext.close(HELP_PANEL_ID);
		// Reset the panel content based on route
		if (location) {
			const { routeName, routeGroup } = getRouteData(location.pathname);
			setRouteData({ routeName, routeGroup });
		}

		confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);

		rightSidebarContext.triggerButtonRef?.current?.focus();
	};

	const handleOnCloseAiMatePanel = () => {
		rightSidebarContext.close(CONVERSATION_ASSISTANT_CONTAINER_ID);
		confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);
	};

	const handleOnCloseIssueCreateSidePanel = (panelId: string) => {
		rightSidebarContext.close(panelId);
	};

	const handleOnSetArticleId = (articleId: string) => {
		inProductHelpContext.setArticleId(articleId);
		if (articleId) {
			confluenceLocalStorageInstance.setItem(
				keys.RIGHT_SIDEBAR,
				JSON.stringify({
					panelId: HELP_PANEL_ID,
					panelWidth: HELP_PANEL_WIDTH,
					articleId,
				}),
			);
		}
	};

	const handleOnOpenHelpPanel = useCallback(
		(event: any) => {
			const { detail } = event;
			rightSidebarContext.open(detail.panelId, detail.width, detail.isMinimized, detail.toTop);
			inProductHelpContext.setArticleId(detail.articleId);
		},
		[rightSidebarContext, inProductHelpContext],
	);

	useEffect(() => {
		window.addEventListener('openHelpPanel', handleOnOpenHelpPanel);

		return () => {
			window.removeEventListener('openHelpPanel', handleOnOpenHelpPanel);
		};
	}, [handleOnOpenHelpPanel]);

	/* UseEffect to call toggleInProductHelp based on rightSidebarCurrentState to toggle between
  iph*/
	useEffect(() => {
		const rightSidebarState = rightSidebarContext.getCurrent();
		if (rightSidebarState && !rightSidebarState.isCollapsed && !rightSidebarState.isMinimized) {
			toggleInProductHelp(true);
		} else {
			toggleInProductHelp(false);
		}
	}, [rightSidebarContext, toggleInProductHelp]);

	const shouldDisplaySidebar =
		rightSidebarCurrentState &&
		!rightSidebarCurrentState.isCollapsed &&
		!rightSidebarCurrentState.isMinimized;

	// Open correct sidebar and set localstorage value to persist the panel across refreshes
	const getRightSidePanelElement = () => {
		if (!rightSidebarCurrentState) {
			return;
		}
		const { panelId } = rightSidebarCurrentState;

		if (isRovoEnabled && panelId === CONVERSATION_ASSISTANT_CONTAINER_ID) {
			const sidebarStorageItem = JSON.stringify({
				panelId: CONVERSATION_ASSISTANT_CONTAINER_ID,
				panelWidth: CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
			});
			confluenceLocalStorageInstance.setItem(keys.RIGHT_SIDEBAR, sidebarStorageItem);
			return (
				<ErrorBoundary attribution={Attribution.AI_MATE}>
					<PanelSplitter
						label={intl.formatMessage(i18n.panelSplitter)}
						onResizeEnd={handleRovoPanelResizeComplete}
					/>
					<ConfluenceConversationAssistant onClosePanel={handleOnCloseAiMatePanel} />
				</ErrorBoundary>
			);
		}

		if (panelId === ISSUE_CREATE_SIDE_PANEL_ID) {
			return (
				<ErrorBoundary attribution={Attribution.INTEGRATIONS}>
					<IssueCreateSidePanel onClose={() => handleOnCloseIssueCreateSidePanel(panelId)} />
				</ErrorBoundary>
			);
		}

		if (panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID) {
			return (
				<ErrorBoundary attribution={Attribution.INTEGRATIONS}>
					<BulkCreateSidePanel onClose={() => handleOnCloseIssueCreateSidePanel(panelId)} />
				</ErrorBoundary>
			);
		}

		if (panelId === AUDIT_LOG_DETAILS_PANEL) {
			return (
				<ErrorBoundary attribution={Attribution.AUDIT_LOG}>
					<AuditLogDetailsPanel />
				</ErrorBoundary>
			);
		}

		if (panelId === APPROVALS_PANE_ID) {
			return (
				<ErrorBoundary attribution={Attribution.CC_IMPORTER}>
					<ApprovalsPane />
				</ErrorBoundary>
			);
		}

		if (panelId === HELP_PANEL_ID) {
			confluenceLocalStorageInstance.setItem(
				keys.RIGHT_SIDEBAR,
				JSON.stringify({
					panelId: HELP_PANEL_ID,
					panelWidth: HELP_PANEL_WIDTH,
					articleId,
					routeName: routeData.routeName,
					routeGroup: routeData.routeGroup,
				}),
			);

			return (
				<ErrorBoundary attribution={Attribution.COLLABORATION}>
					<HelpPanel
						isOpen
						articleId={articleId}
						push={push}
						routeGroup={routeData.routeGroup}
						routeName={routeData.routeName}
						onCloseHelpPanel={handleOnCloseHelpPanel}
						onSetArticleId={handleOnSetArticleId}
					/>
				</ErrorBoundary>
			);
		}
	};

	if (!shouldDisplaySidebar) return null;

	return (
		<Panel defaultWidth={rightSidebarCurrentState?.width || 0} testId="right-sidebar-panel">
			{getRightSidePanelElement()}
		</Panel>
	);
};
